(() => {
  const SELECTOR = '[data-dt-lrp]';
  const now = () => Date.now();

  function getShopKey() {
    return (window.Shopify && window.Shopify.shop) ? window.Shopify.shop : location.hostname;
  }

  function parseCooldown(value) {
    switch (value) {
      case 'session': return { type: 'session' };
      case '1d': return { type: 'ms', ms: 24 * 60 * 60 * 1000 };
      case '7d': return { type: 'ms', ms: 7 * 24 * 60 * 60 * 1000 };
      case '30d': return { type: 'ms', ms: 30 * 24 * 60 * 60 * 1000 };
      default: return { type: 'ms', ms: 7 * 24 * 60 * 60 * 1000 };
    }
  }

  function hasCooldownActive(key, cooldown) {
    try {
      if (cooldown.type === 'session') return sessionStorage.getItem(key) === '1';
      const ts = localStorage.getItem(key);
      if (!ts) return false;
      return (now() - Number(ts)) < cooldown.ms;
    } catch { return false; }
  }

  function setCooldown(key, cooldown) {
    try {
      if (cooldown.type === 'session') sessionStorage.setItem(key, '1');
      else localStorage.setItem(key, String(now()));
    } catch {}
  }

  function normalizeCsv(str) {
    return (str || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
  }

  function normalizeLines(str) {
    return (str || '')
      .split('\n')
      .map(s => s.trim())
      .filter(Boolean);
  }

  // Mejora #1: targeting
  function isTargeted(rootEl) {
    const mode = (rootEl.getAttribute('data-targeting-mode') || 'all').trim();
    if (mode === 'all') return true;

    const pageType = (rootEl.getAttribute('data-page-type') || '').trim();
    const handle = (rootEl.getAttribute('data-page-handle') || '').trim();
    const pathname = (rootEl.getAttribute('data-pathname') || location.pathname).trim();

    if (mode === 'page_types') {
      const allowed = normalizeCsv(rootEl.getAttribute('data-target-page-types'));
      return allowed.length ? allowed.includes(pageType) : true;
    }

    if (mode === 'handles') {
      const allowed = normalizeCsv(rootEl.getAttribute('data-target-handles'));
      if (!allowed.length) return true;
      if (!handle) return false;
      return allowed.includes(handle);
    }

    if (mode === 'paths') {
      const prefixes = normalizeLines(rootEl.getAttribute('data-target-paths'));
      if (!prefixes.length) return true;
      return prefixes.some(pref => pathname.startsWith(pref));
    }

    return true;
  }

  // Mejora #2: allow/deny
  function isAllowedByLists(rootEl, suggestedCountry, suggestedLanguage) {
    const allowCountries = normalizeCsv(rootEl.getAttribute('data-allow-countries')).map(s => s.toUpperCase());
    const denyCountries  = normalizeCsv(rootEl.getAttribute('data-deny-countries')).map(s => s.toUpperCase());
    const allowLanguages = normalizeCsv(rootEl.getAttribute('data-allow-languages')).map(s => s.toLowerCase());
    const denyLanguages  = normalizeCsv(rootEl.getAttribute('data-deny-languages')).map(s => s.toLowerCase());

    const c = (suggestedCountry || '').toUpperCase();
    const l = (suggestedLanguage || '').toLowerCase();

    if (denyCountries.includes(c)) return false;
    if (denyLanguages.includes(l)) return false;

    if (allowCountries.length && !allowCountries.includes(c)) return false;
    if (allowLanguages.length && !allowLanguages.includes(l)) return false;

    return true;
  }

  // Validación extra: solo si existe en la tienda (Markets/idiomas habilitados)
  function existsInStore(rootEl, suggestedCountry, suggestedLanguage) {
    const availableCountries = normalizeCsv(rootEl.getAttribute('data-available-countries')).map(s => s.toUpperCase());
    const availableLanguages = normalizeCsv(rootEl.getAttribute('data-available-languages')).map(s => s.toLowerCase());

    const c = (suggestedCountry || '').toUpperCase();
    const l = (suggestedLanguage || '').toLowerCase();

    if (c && availableCountries.length && !availableCountries.includes(c)) return false;
    if (l && availableLanguages.length && !availableLanguages.includes(l)) return false;

    return true;
  }

  // Endpoint oficial de localization discovery  [oai_citation:5‡Shopify](https://shopify.dev/docs/storefronts/themes/markets/localization-discovery?utm_source=chatgpt.com)
  async function fetchSuggestions({ excludeCountry, excludeLanguage }) {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ? window.Shopify.routes.root : '/';

    const url =
      root +
      'browsing_context_suggestions.json' +
      '?country[enabled]=true' +
      `&country[exclude]=${encodeURIComponent(excludeCountry || '')}` +
      '&language[enabled]=true' +
      `&language[exclude]=${encodeURIComponent(excludeLanguage || '')}`;

    const res = await fetch(url, { credentials: 'same-origin' });
    if (!res.ok) return null;
    return await res.json();
  }

  function decideMismatch({ mode, currentCountry, currentLanguage, suggestedCountry, suggestedLanguage }) {
    const cCur = (currentCountry || '').toUpperCase();
    const lCur = (currentLanguage || '').toLowerCase();
    const cSug = (suggestedCountry || '').toUpperCase();
    const lSug = (suggestedLanguage || '').toLowerCase();

    const countryDiff = cSug && cCur && cSug !== cCur;
    const langDiff = lSug && lCur && lSug !== lCur;

    if (mode === 'country_only') return countryDiff;
    if (mode === 'language_only') return langDiff;
    return countryDiff || langDiff; // country_language
  }

  function openModal(rootEl) {
    rootEl.hidden = false;
    document.documentElement.classList.add('dt-lrp--open');
    const dialog = rootEl.querySelector('.dt-lrp__dialog');
    dialog && dialog.focus({ preventScroll: true });
  }

  function closeModal(rootEl) {
    rootEl.hidden = true;
    document.documentElement.classList.remove('dt-lrp--open');
  }

  // Cambiar contexto vía /localization (form localization)  [oai_citation:6‡Shopify](https://shopify.dev/docs/api/liquid/tags/form?utm_source=chatgpt.com)
  function submitLocalization({ country, language, returnTo }) {
    const form = document.createElement('form');
    form.action = '/localization';
    form.method = 'POST';
    form.hidden = true;

    const m = document.createElement('input');
    m.name = '_method';
    m.value = 'PUT';
    form.appendChild(m);

    if (country) {
      const c = document.createElement('input');
      c.name = 'country_code';
      c.value = country;
      form.appendChild(c);
    }

    if (language) {
      const l = document.createElement('input');
      l.name = 'language_code';
      l.value = language;
      form.appendChild(l);
    }

    if (returnTo) {
      const r = document.createElement('input');
      r.name = 'return_to';
      r.value = returnTo;
      form.appendChild(r);
    }

    document.body.appendChild(form);
    form.submit();
  }

  function initOne(rootEl) {
    const shopKey = getShopKey();
    const storageKey = `dt_lrp_dismissed_${shopKey}`;

    const mode = rootEl.getAttribute('data-mode') || 'country_language';
    const delayMs = Number(rootEl.getAttribute('data-delay-ms') || '0');
    const cooldown = parseCooldown(rootEl.getAttribute('data-cooldown') || '7d');

    const showOverlay = rootEl.getAttribute('data-show-overlay') === 'true';
    const overlayClose = rootEl.getAttribute('data-overlay-close') === 'true';

    const currentCountry = rootEl.getAttribute('data-current-country') || '';
    const currentLanguage = rootEl.getAttribute('data-current-language') || '';

    // Mejora #1
    if (!isTargeted(rootEl)) return;

    // Cooldown
    if (hasCooldownActive(storageKey, cooldown)) return;

    // Overlay
    const overlay = rootEl.querySelector('[data-dt-lrp-overlay]');
    if (overlay) {
      overlay.style.display = showOverlay ? '' : 'none';
      if (overlayClose) overlay.addEventListener('click', () => {
        setCooldown(storageKey, cooldown);
        closeModal(rootEl);
      });
    }

    // Dismiss buttons
    rootEl.querySelectorAll('[data-dt-lrp-dismiss]').forEach(btn => {
      btn.addEventListener('click', () => {
        setCooldown(storageKey, cooldown);
        closeModal(rootEl);
      });
    });

    const acceptBtn = rootEl.querySelector('[data-dt-lrp-accept]');

    fetchSuggestions({ excludeCountry: currentCountry, excludeLanguage: currentLanguage })
      .then(data => {
        if (!data || !Array.isArray(data.suggestions) || !data.suggestions.length) return;

        const parts = data.suggestions[0]?.parts;
        if (!parts) return;

        const suggestedCountry = parts.country?.handle || '';
        const suggestedLanguage = parts.language?.handle || '';

        // Validación extra: debe existir en la tienda
        if (!existsInStore(rootEl, suggestedCountry, suggestedLanguage)) return;

        // ✅ Si está en la versión correcta => NO se muestra
        const mismatch = decideMismatch({
          mode,
          currentCountry,
          currentLanguage,
          suggestedCountry,
          suggestedLanguage
        });
        if (!mismatch) return;

        // Mejora #2
        if (!isAllowedByLists(rootEl, suggestedCountry, suggestedLanguage)) return;

        // Mejora #3: hint premium
        const hint = rootEl.querySelector('[data-dt-lrp-hint]');
        const cName = rootEl.querySelector('[data-dt-lrp-country-name]');
        const lName = rootEl.querySelector('[data-dt-lrp-language-name]');
        const sep = rootEl.querySelector('[data-dt-lrp-sep]');

        const countryName = parts.country?.name || '';
        const languageName = parts.language?.name || '';

        if (hint && (countryName || languageName)) {
          if (cName) cName.textContent = countryName;
          if (lName) lName.textContent = languageName;
          if (sep) sep.style.display = (countryName && languageName) ? '' : 'none';
          hint.hidden = false;
        }

        if (acceptBtn) {
          acceptBtn.addEventListener('click', () => {
            setCooldown(storageKey, cooldown);

            const returnTo = location.pathname + location.search;
            const targetCountry = (mode === 'language_only') ? currentCountry : (suggestedCountry || currentCountry);
            const targetLanguage = (mode === 'country_only') ? currentLanguage : (suggestedLanguage || currentLanguage);

            submitLocalization({
              country: targetCountry,
              language: targetLanguage,
              returnTo
            });
          }, { once: true });
        }

        window.setTimeout(() => openModal(rootEl), delayMs);
      })
      .catch(() => {});
  }

  function initAll() {
    document.querySelectorAll(SELECTOR).forEach(initOne);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
})();